import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TextField, Spinner, Pagination, Select, Checkbox, Button, Banner, Tag, InlineStack, Text } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useAuth, useAuthFetch } from './AuthComponent';
import { useStore } from './StoreContext';
import StockProductModal from './StockProductModal';
import StockBulkModal from './StockBulkModal';

function StatePill({ state, active, total }) {
    if (state === 'fully_stocked')
        return <span className="state-pill state-pill--ok">Fully stocked</span>;
    if (state === 'partial')
        return <span className="state-pill state-pill--warn">Partial {active} of {total}</span>;
    return <span className="state-pill state-pill--neutral">Not stocked</span>;
}

function SortableTh({ keyId, label, sortKey, sortDir, onSort, align }) {
    const active = sortKey === keyId;
    const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '';
    return (
        <th
            className={'sp-table__th-sort' + (active ? ' sp-table__th-sort--active' : '')}
            style={align === 'center' ? { textAlign: 'center' } : undefined}
            onClick={() => onSort(keyId)}
            aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <span className="sp-table__th-label">{label}</span>
            <span className="sp-table__th-arrow" aria-hidden="true">{arrow}</span>
        </th>
    );
}

export default function StockProducts() {
    const { user, emulating } = useAuth();
    const { activeStore } = useStore();
    const storeFetch = useAuthFetch();

    // Stock state (variant active/inactive, bins) is per-location. Without a
    // location scope the search would be meaningless, so gate the page the
    // same way bulk print is gated in OrdersTanStack.
    const inLocationScope = !!emulating || (user?.locations?.length === 1);

    // Search query is mirrored in the URL (?q=...) so the page is shareable.
    const [searchParams, setSearchParams] = useSearchParams();
    const initialQ = searchParams.get('q') || '';
    const initialBy = searchParams.get('by') === 'tag' ? 'tag' : 'default';
    const [query, setQuery]       = useState(initialQ);
    const [debounced, setDebounced] = useState(initialQ.trim());
    const [searchBy, setSearchBy] = useState(initialBy);
    const [results, setResults]   = useState([]);
    const [location, setLocation] = useState(null);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState(null);
    const [openProductId, setOpenProductId] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [page, setPage] = useState(1);
    const [cursors, setCursors] = useState({ title: null, variants: null });
    const [hasMore, setHasMore] = useState(false);
    // Tracks the query for which a fetch has actually completed. The empty-state
    // message ("No products match …") only shows once this matches `debounced`,
    // so users don't see a flash of "no results" during the gap between the
    // debounce setter clearing results and the fetch effect setting loading=true.
    const [lastFetchedQuery, setLastFetchedQuery] = useState('');
    const lastFlashedRef = useRef(null);
    const [, forceRerender] = useState(0);
    const flashSetRef = useRef(new Set());  // ids currently flashing post-bulk

    // ── Multi-select + bulk-stock state ──────────────────────────────
    const [sel, setSel] = useState(() => new Set());
    const [allMatchingMode, setAllMatchingMode] = useState(false); // "Select all N matching"
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkBanner, setBulkBanner] = useState(null);  // top success/warning banner
    const lastIdxRef = useRef(null);   // anchor for shift-range
    const shiftRef = useRef(false);    // shiftKey at mousedown

    // ── Column sort state (null = natural search-result order) ─────────
    const [sortKey, setSortKey] = useState(null);    // 'product'|'sku'|'vendor'|'variants'|'status'
    const [sortDir, setSortDir] = useState('asc');   // 'asc' | 'desc'
    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
        // Reset shift anchor + pagination anchor when the row order changes.
        lastIdxRef.current = null;
    };

    // ── Sticky chrome height (so wide-table thead can sit beneath it) ──
    const chromeRef = useRef(null);
    const [chromeH, setChromeH] = useState(0);
    useEffect(() => {
        const el = chromeRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => setChromeH(el.offsetHeight));
        ro.observe(el);
        setChromeH(el.offsetHeight);
        return () => ro.disconnect();
    });

    // Debounce 300ms
    useEffect(() => {
        const t = setTimeout(() => {
            setDebounced(query.trim());
            setPage(1);
            setResults([]);
            setCursors({ title: null, variants: null });
            setHasMore(false);
            // Clearing the search abandons any prior selection — the underlying
            // result set is changing.
            setSel(new Set());
            setAllMatchingMode(false);
            lastIdxRef.current = null;
        }, 300);
        return () => clearTimeout(t);
    }, [query]);

    // Mirror the debounced query into the URL (?q=...&by=tag) so the page is
    // shareable.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        let changed = false;
        if (debounced) {
            if (next.get('q') !== debounced) { next.set('q', debounced); changed = true; }
        } else if (next.has('q')) { next.delete('q'); changed = true; }
        if (searchBy === 'tag') {
            if (next.get('by') !== 'tag') { next.set('by', 'tag'); changed = true; }
        } else if (next.has('by')) { next.delete('by'); changed = true; }
        if (changed) setSearchParams(next, { replace: true });
    }, [debounced, searchBy]); // eslint-disable-line

    useEffect(() => {
        const urlQ = searchParams.get('q') || '';
        if (urlQ !== query) {
            setQuery(urlQ);
            setDebounced(urlQ.trim());
        }
    }, [searchParams]); // eslint-disable-line

    // Fetch on debounced change
    useEffect(() => {
        if (!debounced || debounced.length < 2) {
            setResults([]); setError(null);
            setCursors({ title: null, variants: null });
            setHasMore(false);
            setLastFetchedQuery('');
            return;
        }
        const abort = new AbortController();
        setLoading(true); setError(null);
        storeFetch(`/api/products/search?q=${encodeURIComponent(debounced)}&search_by=${searchBy}`, { signal: abort.signal })
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(data => {
                setLocation(data.location);
                setResults(data.results || []);
                setCursors({
                    title:    data.next_title_cursor || null,
                    variants: data.next_variants_cursor || null,
                });
                setHasMore(!!data.has_more);
                setLastFetchedQuery(debounced);
            })
            .catch(err => { if (err.name !== 'AbortError') setError(err.message || 'Search failed'); })
            .finally(() => setLoading(false));
        return () => abort.abort();
    }, [debounced, searchBy, emulating?.id, activeStore, retryCount]); // eslint-disable-line

    const [loadingMore, setLoadingMore] = useState(false);
    const loadMore = useCallback(() => {
        if (loadingMore || !hasMore || !debounced) return;
        setLoadingMore(true);
        const params = new URLSearchParams({ q: debounced, search_by: searchBy });
        if (cursors.title)    params.set('title_cursor', cursors.title);
        if (cursors.variants) params.set('variants_cursor', cursors.variants);
        storeFetch(`/api/products/search?${params.toString()}`)
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(data => {
                let freshCount = 0;
                setResults(rs => {
                    const seen = new Set(rs.map(r => r.id));
                    const fresh = (data.results || []).filter(r => !seen.has(r.id));
                    freshCount = fresh.length;
                    return [...rs, ...fresh];
                });
                setCursors({
                    title:    data.next_title_cursor || null,
                    variants: data.next_variants_cursor || null,
                });
                setHasMore(!!data.has_more && freshCount > 0);
            })
            .catch(err => setError(err.message || 'Load more failed'))
            .finally(() => setLoadingMore(false));
    }, [loadingMore, hasMore, debounced, searchBy, cursors.title, cursors.variants, storeFetch]);

    useEffect(() => {
        if (loadingMore || !hasMore || results.length === 0) return;
        const loaded = Math.max(1, Math.ceil(results.length / 25));
        if (page >= loaded - 2) {
            loadMore();
        }
    }, [page, results.length, hasMore, loadingMore, loadMore]);

    // Reload a single product's state after the modal saves (or other mutation
    // arrives). Updates that one row in-place + flashes it.
    const refreshResultAndFlash = (productId, newState) => {
        setResults(rs => rs.map(r => r.id === productId ? { ...r, ...newState } : r));
        lastFlashedRef.current = productId;
        setTimeout(() => {
            if (lastFlashedRef.current === productId) lastFlashedRef.current = null;
        }, 1500);
    };

    const sortedResults = useMemo(() => {
        if (!sortKey) return results;
        const stateOrder = { not_stocked: 0, partial: 1, fully_stocked: 2 };
        const keyOf = {
            product:  r => (r.title || '').toLowerCase(),
            sku:      r => (r.sku_base || '').toLowerCase(),
            vendor:   r => (r.vendor || '').toLowerCase(),
            variants: r => r.variant_count || 0,
            status:   r => stateOrder[r.state] ?? -1,
        }[sortKey];
        const arr = [...results];
        arr.sort((a, b) => {
            const av = keyOf(a);
            const bv = keyOf(b);
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            // Tie-break by title so the order is deterministic across renders.
            const ta = (a.title || '').toLowerCase();
            const tb = (b.title || '').toLowerCase();
            return ta < tb ? -1 : ta > tb ? 1 : 0;
        });
        return arr;
    }, [results, sortKey, sortDir]);

    const pageSize = 25;
    const loadedPages = Math.max(1, Math.ceil(sortedResults.length / pageSize));
    const totalPages  = hasMore ? loadedPages + 1 : loadedPages;
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * pageSize;
    const pageEnd   = Math.min(pageStart + pageSize, sortedResults.length);
    const pageResults = sortedResults.slice(pageStart, pageEnd);
    const pageIds = pageResults.map(r => r.id);

    // ── Selection derived state ─────────────────────────────────────────
    const allPageSelected = pageIds.length > 0 && pageIds.every(id => sel.has(id));
    const somePageSelected = pageIds.some(id => sel.has(id));
    const selectedCount = allMatchingMode
        ? (hasMore ? '~' + results.length + '+' : results.length)
        : sel.size;
    const allLoadedSelected = results.length > 0 && results.every(r => sel.has(r.id));
    // Show "select all matching" prompt when full page is selected and there
    // are more results (either un-paged or un-loaded).
    const showSelectAllPrompt =
        !allMatchingMode && allPageSelected
        && (results.length > pageIds.length || hasMore);

    // Reset shift anchor when page changes.
    useEffect(() => { lastIdxRef.current = null; }, [currentPage]);

    const toggleOne = (idx, ev) => {
        const id = pageResults[idx].id;
        const willSelect = !sel.has(id);
        const n = new Set(sel);
        const isShift = ev && ev.shiftKey;
        if (isShift && lastIdxRef.current != null) {
            const a = Math.min(lastIdxRef.current, idx);
            const b = Math.max(lastIdxRef.current, idx);
            for (let i = a; i <= b; i++) {
                const rid = pageResults[i].id;
                if (willSelect) n.add(rid); else n.delete(rid);
            }
        } else {
            if (willSelect) n.add(id); else n.delete(id);
        }
        lastIdxRef.current = idx;
        setSel(n);
        if (allMatchingMode) setAllMatchingMode(false);
    };
    const toggleMaster = () => {
        const n = new Set(sel);
        if (allPageSelected) pageIds.forEach(id => n.delete(id));
        else pageIds.forEach(id => n.add(id));
        setSel(n);
        if (allMatchingMode) setAllMatchingMode(false);
    };
    const selectAllMatching = () => {
        setAllMatchingMode(true);
        setSel(new Set(results.map(r => r.id))); // covers loaded pages; server walks the rest by query
    };
    const clearSelection = () => {
        setSel(new Set());
        setAllMatchingMode(false);
        lastIdxRef.current = null;
    };

    // ── Bulk run result ─────────────────────────────────────────────────
    const onBulkComplete = ({ successIds, totals, failures }) => {
        // Update affected rows in place to fully_stocked + flash.
        if (successIds && successIds.length) {
            setResults(rs => rs.map(r => successIds.includes(r.id)
                ? { ...r, state: 'fully_stocked', variants_active_at_loc: r.variant_count }
                : r));
            flashSetRef.current = new Set(successIds);
            forceRerender(x => x + 1);
            setTimeout(() => {
                flashSetRef.current = new Set();
                forceRerender(x => x + 1);
            }, 1600);
        }
        clearSelection();
        setBulkBanner({ totals, failureCount: (failures || []).length });
        setTimeout(() => setBulkBanner(b => (b && b.totals === totals ? null : b)), 9000);
    };

    // Computed numbers for the confirm modal.
    const bulkSelectedProducts = allMatchingMode
        ? results
        : results.filter(r => sel.has(r.id));
    const bulkTotalCount = allMatchingMode && hasMore
        ? results.length  // approximate; backend walks the actual full query
        : bulkSelectedProducts.length;
    // Only count variants that would ACTUALLY be activated — i.e. the
    // tracked, currently-inactive ones. Already-fully-stocked products (incl.
    // untracked-everywhere products that are sellable by default) contribute
    // 0 to this total.
    const bulkVariantTotal = bulkSelectedProducts.reduce((n, p) => {
        if (p.state === 'fully_stocked') return n;
        const remaining = (p.variant_count || 0) - (p.variants_active_at_loc || 0);
        return n + Math.max(0, remaining);
    }, 0);
    const bulkSkipCount = bulkSelectedProducts.filter(p => p.state === 'fully_stocked').length;

    const isFlashing = (id) => flashSetRef.current.has(id) || lastFlashedRef.current === id;

    if (!inLocationScope) {
        return (
            <div className="stock-products-page">
                <div className="sticky-page-header">
                    <h1 className="page-title">Stock Products</h1>
                </div>
                <div style={{ padding: 24, maxWidth: 760, margin: '0 auto' }}>
                    <div style={{
                        background: '#fff4e5',
                        border: '1px solid #f4c777',
                        borderRadius: 8,
                        padding: '16px 20px',
                        color: '#7a4a00',
                    }}>
                        You can't stock products when you are viewing "All Locations". Choose a single location first.
                    </div>
                </div>
            </div>
        );
    }

    const wrapClass = 'sp-wrap sp-wrap--wide';
    const locationName = location?.location_name || '';

    return (
        <div className="stock-products-page" style={{ ['--sp-chrome-h']: chromeH + 'px' }}>
            <div className="sp-chrome" ref={chromeRef}>
                <div className="sticky-page-header">
                    <h1 className="page-title">Stock Products</h1>
                    {locationName && (
                        <div className="page-subtitle">
                            Showing results at: <strong>{locationName}</strong>
                        </div>
                    )}
                </div>

                <div className="stock-search-bar">
                    <div className="stock-search-bar__inner" style={{ maxWidth: 1100 }}>
                        <div className="stock-search-bar__field">
                            <TextField
                                value={query}
                                onChange={setQuery}
                                placeholder={searchBy === 'tag'
                                    ? "Search by tag…"
                                    : "Search by name, SKU, or barcode…"}
                                prefix={<SearchIcon width={16} height={16} />}
                                suffix={loading ? <Spinner size="small" /> : null}
                                clearButton
                                onClearButtonClick={() => setQuery('')}
                                autoComplete="off"
                            />
                        </div>
                        <div className="stock-search-bar__by">
                            <Select label="Search by" labelHidden
                                    options={[
                                        { label: 'Name, SKU, barcode', value: 'default' },
                                        { label: 'Tag',                value: 'tag' },
                                    ]}
                                    value={searchBy}
                                    onChange={setSearchBy} />
                        </div>
                    </div>
                    {/* Active-tag chip: surfaces the literal string we're
                        passing to Shopify's tag search. Note: Shopify's
                        `tag:"foo"` does SUBSTRING-within-tag matching, not
                        strict tag equality — so a tag like "club: Park
                        Valley United FC 2026-2027" matches a search for
                        "club: Park Valley". The label below reflects
                        that distinction. Click the × to clear. */}
                    {searchBy === 'tag' && debounced.trim() && (
                        <div className="stock-search-bar__chip-row"
                             style={{ marginTop: 8, maxWidth: 1100 }}>
                            <InlineStack gap="200" blockAlign="center">
                                <Text variant="bodySm" tone="subdued">
                                    Matching tags containing exact string:
                                </Text>
                                <Tag onRemove={() => setQuery('')}>
                                    {debounced.trim()}
                                </Tag>
                            </InlineStack>
                        </div>
                    )}
                </div>

                {results.length > 0 && (
                    <>
                        <div className="sp-selectbar">
                            <Checkbox
                                label="Select all on page"
                                labelHidden
                                checked={allPageSelected ? true : (somePageSelected ? 'indeterminate' : false)}
                                onChange={toggleMaster}
                            />
                            <span className="sp-selectbar__count">
                                {sel.size > 0
                                    ? <><strong>{selectedCount}</strong> selected</>
                                    : <>{results.length}{hasMore ? '+' : ''} results · showing {pageStart + 1}–{pageEnd}</>}
                            </span>
                            {sel.size > 0 && (
                                <Button variant="plain" onClick={clearSelection}>Clear</Button>
                            )}
                            <span className="sp-selectbar__spacer" />
                            <span className="sp-selectbar__hint">Tip: Shift-click to select a range</span>
                        </div>

                        {showSelectAllPrompt && (
                            <div className="sp-selectall-prompt">
                                <span>All {pageIds.length} on this page selected.</span>
                                <button className="sp-selectall-prompt__link" onClick={selectAllMatching}>
                                    Select all {results.length}{hasMore ? '+' : ''} matching results
                                </button>
                            </div>
                        )}
                        {allMatchingMode && (
                            <div className="sp-selectall-prompt">
                                <span>
                                    All <strong>{results.length}{hasMore ? '+' : ''}</strong> matching results are selected.
                                </span>
                                <button className="sp-selectall-prompt__link" onClick={clearSelection}>
                                    Clear selection
                                </button>
                            </div>
                        )}

                        {sel.size > 0 && (
                            <div className="sp-bulkbar">
                                <span className="sp-bulkbar__count">
                                    {selectedCount} {sel.size === 1 ? 'product' : 'products'} selected
                                </span>
                                <span className="sp-bulkbar__spacer" />
                                <div className="sp-bulkbar__actions">
                                    {sel.size === 1 && !allMatchingMode && (
                                        <Button onClick={() => setOpenProductId([...sel][0])}>
                                            Set quantity…
                                        </Button>
                                    )}
                                    <Button variant="primary" tone="critical" onClick={() => setBulkOpen(true)}>
                                        Mark as stocked at {locationName}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {bulkBanner && (
                <div className="sp-results-banner-wrap">
                    <Banner
                        tone={bulkBanner.failureCount ? 'warning' : 'success'}
                        onDismiss={() => setBulkBanner(null)}
                    >
                        Enabled <strong>{bulkBanner.totals.enabled || 0}</strong>{' '}
                        {(bulkBanner.totals.enabled || 0) === 1 ? 'product' : 'products'}{' '}
                        (<strong>{bulkBanner.totals.variants_enabled || 0}</strong> variants){locationName && <> at <strong>{locationName}</strong></>}.
                        {(bulkBanner.totals.skipped || 0) > 0 && <> {bulkBanner.totals.skipped} already stocked.</>}
                        {bulkBanner.failureCount > 0 && <> {bulkBanner.failureCount} failed.</>}
                    </Banner>
                </div>
            )}

            {error && (
                <div style={{ padding: 16, color: '#b71d1d' }}>
                    Search error: {error} <button onClick={() => setRetryCount(c => c + 1)}>Retry</button>
                </div>
            )}

            {!error && debounced.length >= 2 && !loading && lastFetchedQuery === debounced && results.length === 0 && (
                <div style={{ padding: 16, color: '#6d7175', maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
                    No products match "{debounced}". Try a different {searchBy === 'tag' ? 'tag' : 'name, SKU, or barcode'}.
                </div>
            )}

            {!error && debounced.length < 2 && (
                <div style={{ padding: 16, color: '#6d7175', maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
                    Start typing to search the catalog.
                </div>
            )}

            {results.length > 0 && (
                <div className={wrapClass}>
                    <table className="sp-table">
                        <thead>
                            <tr>
                                <th className="sp-table__check">
                                    <Checkbox
                                        label="Select all on page"
                                        labelHidden
                                        checked={allPageSelected ? true : (somePageSelected ? 'indeterminate' : false)}
                                        onChange={toggleMaster}
                                    />
                                </th>
                                <SortableTh keyId="product"  label="Product"            sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                <SortableTh keyId="sku"      label="SKU"                sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                <SortableTh keyId="vendor"   label="Vendor"             sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                <SortableTh keyId="variants" label="Variants"           sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="center" />
                                <SortableTh keyId="status"   label="Status at location" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {pageResults.map((p, idx) => (
                                <tr key={p.id}
                                    className={`${sel.has(p.id) ? 'is-selected' : ''}${isFlashing(p.id) ? ' is-flash' : ''}`}
                                    onClick={() => setOpenProductId(p.id)}>
                                    <td className="sp-table__check"
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => { shiftRef.current = e.shiftKey; }}>
                                        <Checkbox
                                            label={`Select ${p.title}`}
                                            labelHidden
                                            checked={sel.has(p.id)}
                                            onChange={(_v, _id, ev) => toggleOne(idx, ev || { shiftKey: shiftRef.current })}
                                        />
                                    </td>
                                    <td>
                                        <div className="sp-table__prod">
                                            {p.image
                                                ? <img src={p.image} alt="" className="sp-table__thumb" />
                                                : <div className="sp-table__thumb sp-table__thumb--blank" />}
                                            <span className="sp-table__title">{p.title}</span>
                                        </div>
                                    </td>
                                    <td className="sp-cell-mono">{p.sku_base || ''}</td>
                                    <td className="sp-table__vendor">{p.vendor || ''}</td>
                                    <td style={{ textAlign: 'center' }}>{p.variant_count || 0}</td>
                                    <td>
                                        <StatePill state={p.state}
                                            active={p.variants_active_at_loc}
                                            total={p.variant_count} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="stock-pagination">
                        <div className="stock-pagination__summary">
                            Showing {pageStart + 1}–{pageEnd}
                        </div>
                        {totalPages > 1 && (
                            <Pagination
                                hasPrevious={currentPage > 1}
                                onPrevious={() => setPage(Math.max(1, currentPage - 1))}
                                hasNext={currentPage < loadedPages || (hasMore && !loadingMore)}
                                onNext={() => {
                                    if (currentPage < loadedPages) {
                                        setPage(currentPage + 1);
                                    } else if (hasMore && !loadingMore) {
                                        loadMore();
                                        setPage(currentPage + 1);
                                    }
                                }}
                                label={loadingMore
                                    ? `Loading…`
                                    : `Page ${currentPage} of ${hasMore ? loadedPages + '+' : loadedPages}`}
                            />
                        )}
                    </div>
                </div>
            )}

            <StockProductModal
                productId={openProductId}
                open={!!openProductId}
                onClose={() => setOpenProductId(null)}
                onSaved={(updated) => {
                    refreshResultAndFlash(updated.id, {
                        state: updated.state,
                        variants_active_at_loc: updated.variants_active_at_loc,
                        variant_count: updated.variant_count,
                    });
                    setOpenProductId(null);
                }}
            />

            <StockBulkModal
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                mode={allMatchingMode ? 'query' : 'ids'}
                q={debounced}
                searchBy={searchBy}
                productIds={[...sel]}
                totalCount={bulkTotalCount}
                variantTotal={bulkVariantTotal}
                skipCount={bulkSkipCount}
                location={locationName}
                onComplete={onBulkComplete}
            />
        </div>
    );
}
